import { formatDateOnlyShort } from '@/app/utils/formatDate';
import { formatMileage } from '@/features/vehiclesearch/utils/mileage';
import { formatCurrency } from '@/app/utils/formatCurrency';
import {
    BALANCE_LOADING_REASON,
    BALANCE_UNAVAILABLE_REASON,
    excessText,
    exceedsBalance,
} from './invoiceBalance';
import { toScaled } from './invoiceSelection';
import { isReversed } from './invoiceReversal';
import { nextService } from './nextService';
import type { NextService } from './nextService';
import type { InvoiceableService } from './invoiceableLines';
import type { AgreementInvoice, AgreementInvoicesQuery, SoldLines } from '../types/agreements.types';

export type SummaryTileId = 'account_balance' | 'next_service' | 'last_invoiced';

export interface SummaryTile {
    id: SummaryTileId;
    label: string;
    value: string;
    detail: string | null;
}

export const TILE_PLACEHOLDER = '—';
export const NONE_REMAINING = 'None remaining';

export function settledInvoices({
    data,
    isLoading,
    isError,
}: Pick<AgreementInvoicesQuery, 'data' | 'isLoading' | 'isError'>): AgreementInvoice[] | undefined {
    return isLoading || isError ? undefined : data;
}

export function accountBalanceTile(availableBalance: string | null, unreadableReason: string | null): SummaryTile {
    const base = { id: 'account_balance', label: 'Account balance', detail: 'Available to claim' } as const;
    if (unreadableReason === BALANCE_LOADING_REASON) return { ...base, value: 'Checking...' };
    if (unreadableReason === BALANCE_UNAVAILABLE_REASON || availableBalance === null) {
        return { ...base, value: 'Unavailable' };
    }
    return { ...base, value: availableBalance };
}

export function lastInvoicedTile(invoices: AgreementInvoice[] | undefined): SummaryTile {
    const base = { id: 'last_invoiced', label: 'Last invoiced' } as const;
    if (!invoices) return { ...base, value: TILE_PLACEHOLDER, detail: null };
    const latest = invoices.find((invoice) => !isReversed(invoice));
    if (!latest) return { ...base, value: 'Not yet invoiced', detail: null };

    return {
        ...base,
        value: formatDateOnlyShort(latest.invoicedAt),
        detail: latest.mileage === null ? null : `at ${formatMileage(latest.mileage)} miles`,
    };
}

function dueDetail(service: InvoiceableService): string {
    if (service.dueDate) return `${service.name}, due ${formatDateOnlyShort(service.dueDate)}`;
    if (service.dueMileage !== null) return `${service.name}, due at ${formatMileage(service.dueMileage)} miles`;
    return service.name;
}

function nextServiceDetail(
    { service, gross }: NextService,
    cashPosition: number | undefined,
    unreadableReason: string | null,
): string {
    if (unreadableReason !== null || cashPosition === undefined || !exceedsBalance(cashPosition, gross)) {
        return dueDetail(service);
    }
    return `Shortfall: ${excessText(toScaled(gross) - toScaled(cashPosition))} needed`;
}

export function nextServiceTile(
    lines: SoldLines | undefined,
    cashPosition: number | undefined,
    unreadableReason: string | null,
): SummaryTile {
    const base = { id: 'next_service', label: 'Next service' } as const;
    if (!lines) return { ...base, value: TILE_PLACEHOLDER, detail: null };
    const next = nextService(lines);
    if (!next) return { ...base, value: NONE_REMAINING, detail: null };

    return {
        ...base,
        value: formatCurrency(next.gross),
        detail: nextServiceDetail(next, cashPosition, unreadableReason),
    };
}
